import React, { useEffect, useRef, useState, useId, useMemo } from "react";
import { VideoJS, VideoSegmentUrls } from "./VideoJS/VideoJS.js";
import { SeekbarEvents } from "./SeekbarEvents/SeekbarEvents.js";
import apiClient from "../../../../helpers/api-client.js";
import i18n from "../../../../i18n.js";
import videoUnavailableSvg from "./images/recording_unavailable.svg";
import DownloadRequestModal, { DownloadRequestStatusType } from "./DownloadRequestModal.js";
import {
	screencaptureDisplayStateAtom,
	webcamDisplayStateAtom,
	videoDisplayStateAtom,
	videoEndDateAtom,
} from "./store.js";
import { SessionRecordingTimestamps, TIMESTAMP_PLACEHOLDER, VideoEvent } from "../../../utilities/useTimestamps.js";
import RecordingEndedSvg from "./images/RecordingEnded.js";
import { useAtomValue } from "jotai";
import { formatEventTimestamp } from "../../ProctoringResultsPage/ProctoringEventsTable";
import DetectionOverlaySettings, { GearIcon } from "./overlay/DetectionOverlaySettings.js";
import styles from "./ProctoringVideoPlayer.module.css";
import {
	VideoControlButton,
	VolumeControls,
	Seekbar,
	DownloadButton,
	FullScreenButton,
	MoreOptionsButton,
	VideoSpeedButton,
	VideoTimeStamp,
	PlayButton,
} from "./VideoControls/VideoControls.js";
import VideoCard from "./VideoLayout/VideoCard";
import { RecordingType } from "../../../../types/general-types";
import VideoLayout from "./VideoLayout";
import type { ProctoredMediaContainer } from "./hooks";
import SendEmailModal from "./modals/SendEmailModal.js";
import PlayOverlay from "./overlay/PlayOverlay.js";
import VideoControlLayout, { VideoControlButtonWrapper } from "./VideoControls/VideoControlLayout.js";

export type ControlOptions = {
	isPlaying: boolean;
	playbackSpeed: number;
	volume: number;
};

export type SessionNote = {
	id: number | string;
	time: number;
	content: string;
	date: Date;
};

export type VideoSettings = {
	controls: boolean;
	responsive: boolean;
	aspectRatio: string;
	fluid: boolean;
	userActions: {
		click: boolean;
	};
	controlBar: {
		playToggle: boolean;
		volumePanel: boolean;
		progressControl: boolean;
		remainingTimeDisplay: boolean;
	};
};

export type RecordingSourceVideoUrls = Record<number, VideoSegmentUrls>;
export type SessionVideoSources = Partial<Record<RecordingType, RecordingSourceVideoUrls>>;

export type ProctoringVideoPlayerProperties = {
	toggleVolume?: () => void;
	isFullscreen: boolean;
	sessionId: string;
	showTimestamps: boolean;
	/**
	 * TODO move to store
	 */
	controlOptions: ControlOptions;
	videos: SessionVideoSources;
	/**
	 * TODO move to store
	 */
	playback: {
		timer: number;
		lastTime: number;
		skip: boolean;
	};
	/**
	 * TODO move to store
	 */
	playbackDispatch: React.Dispatch<
		| {
				type: "play";
				speed: number;
		  }
		| {
				type: "pause";
		  }
		| {
				type: "skip";
				time: number;
		  }
	>;
	/**
	 * TODO move to store
	 */
	startDate: Date;
	/**
	 * TODO move to store
	 */
	duration: number | undefined;
	/**
	 * TODO move to store
	 */
	timeOffset: number;
	/**
	 *  TODO: move to this component
	 */
	setFullscreen: () => void;
	/**
	 *  TODO: move to this component
	 */
	setPlay: () => void;
	/**
	 *  TODO: move to this component
	 */
	setVolume: React.ChangeEventHandler<HTMLInputElement>;
	/**
	 *  TODO: move to this component
	 */
	setSpeed: (value: string) => void;
	/**
	 *  TODO: move to this component
	 */
	showVideoUnavailable: boolean;
	/**
	 *  TODO: move to this component
	 */
	showAudioThumbnail: boolean;
	/**
	 *  TODO: move to this component
	 */
	webcamVideoOptions: VideoSettings | null | undefined;
	/**
	 *  TODO: move to this component
	 */
	screencaptureVideoOptions: VideoSettings | null | undefined;
	onEmailModalChange?: (showing: boolean) => void;
	/**
	 * Refactor out
	 */
	shouldHideCamVid: boolean;
	/**
	 * Refactor out
	 */
	events: VideoEvent[];
	sessionRecordingTimestamps: SessionRecordingTimestamps;
	/**
	 * Refactor out
	 */
	sessionNotes: SessionNote[];
	/**
	 * Refactor out
	 */
	visibleLiveNotes: SessionNote[];
	/**
	 * Refactor out
	 */
	shouldShowDownload: boolean;

	isWebcamAndScreencaptureDeleted?: boolean;
	webcamAndScreencaptureDeleteCause?: string | null;
	setIsDeleteConfirmationModalShown?: (value: "recording" | "roomscan" | undefined) => void;
	hasInstituteEditPermission?: boolean;
	sessionStatus?: string | undefined | null;
	webCamHasError: boolean;
	setWebCamHasError: (value: boolean) => void;
	screenCaptureHasError: boolean;
	setScreenCaptureHasError: (value: boolean) => void;
	selectedProctoringSessionUuid?: string;
	showMachineLearningOverlay?: boolean;
	proctoredMediaContainer: ProctoredMediaContainer;
};

function hasSources(sources: RecordingSourceVideoUrls) {
	return Object.values(sources).some((segments) => Object.keys(segments).length > 0);
}

function ProctoringVideoPlayer({
	isFullscreen,
	showTimestamps,
	controlOptions,
	playback,
	playbackDispatch,
	videos,
	startDate,
	duration,
	timeOffset,
	setFullscreen,
	setPlay,
	setVolume,
	setSpeed,
	showVideoUnavailable,
	showAudioThumbnail,
	webcamVideoOptions,
	screencaptureVideoOptions,
	onEmailModalChange,
	events,
	sessionRecordingTimestamps,
	sessionNotes,
	visibleLiveNotes,
	shouldShowDownload,
	toggleVolume,
	isWebcamAndScreencaptureDeleted,
	webcamAndScreencaptureDeleteCause,
	setIsDeleteConfirmationModalShown,
	hasInstituteEditPermission,
	sessionStatus,
	webCamHasError,
	setWebCamHasError,
	screenCaptureHasError,
	setScreenCaptureHasError,
	selectedProctoringSessionUuid,
	sessionId,
	showMachineLearningOverlay = false,
	proctoredMediaContainer,
}: ProctoringVideoPlayerProperties) {
	const videoDisplayState = useAtomValue(videoDisplayStateAtom);
	const videoEndDate = useAtomValue(videoEndDateAtom);

	const [emailModalOpen, setEmailModalOpen] = useState(false);
	const [menuShowing, setMenuShowing] = useState(false);
	const [showVideoSpeed, setShowVideoSpeed] = useState(false);
	const controlContainerRef = useRef<HTMLDivElement>(null);
	const playerContainerRef = useRef<HTMLDivElement>(null);

	const [downloadRequestStatus, setDownloadRequestStatus] = useState<DownloadRequestStatusType>("unsent");

	const [isVideoPlayerOverlayVisible, setIsVideoPlayerOverlayVisible] = useState(true);

	const haveAllRecordingsFinishedPlaying = duration !== undefined && playback.timer >= duration - 0.01;

	useEffect(() => {
		setIsVideoPlayerOverlayVisible(!controlOptions.isPlaying);
	}, [controlOptions.isPlaying]);

	useEffect(() => {
		setWebCamHasError(!!isWebcamAndScreencaptureDeleted);
		setScreenCaptureHasError(!!isWebcamAndScreencaptureDeleted);
	}, [isWebcamAndScreencaptureDeleted]);

	let videoOverlayType: string | undefined;
	if (!screenCaptureHasError && !webCamHasError) {
		videoOverlayType = "both";
	} else if (!screenCaptureHasError && webCamHasError) {
		videoOverlayType = "screencapture";
	} else if (screenCaptureHasError && !webCamHasError) {
		videoOverlayType = "webcam";
	}

	// Note - didn't use videoOverlayType because videoOverlayType would be "both" even if it is only screen recording or only webcam
	const hasScreenRecordSource = videos.screencapture && hasSources(videos.screencapture);
	const hasWebcamSource = videos.webcam && hasSources(videos.webcam);
	// Only show fullscreen buttons if there is both webcam and screen recording AND we are in fullscreen

	const settingsButtonRef = useRef<HTMLButtonElement>(null);
	const [settingsPopoverOpen, setSettingsPopoverOpen] = useState(false);
	const settingsPopoverId = useId();

	useEffect(() => {
		const handleResize = () => {
			setSettingsPopoverOpen(false);
		};

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [setSettingsPopoverOpen]);

	const videoTemplate = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(sessionRecordingTimestamps).map(([type, sourceTimestamps]) => [
					type as RecordingType,
					Object.keys(sourceTimestamps).length,
				]),
			),
		[sessionRecordingTimestamps],
	);

	const showSeekbarTime = useMemo(() => {
		const isAnySegmentOk =
			Object.values(proctoredMediaContainer.webcam).some((source) =>
				Object.values(source).some((segment) => segment.ok),
			) ||
			Object.values(proctoredMediaContainer.screencapture).some((source) =>
				Object.values(source).some((segment) => segment.ok),
			);

		const hasWebcamOrScreencaptureError = webCamHasError || screenCaptureHasError;
		const isIncompleteSession = sessionStatus === "incomplete";
		return !hasWebcamOrScreencaptureError && (isAnySegmentOk || !isIncompleteSession);
	}, [proctoredMediaContainer, webCamHasError, screenCaptureHasError, sessionStatus]);

	return (
		<>
			<div className={styles.videoContainer} ref={playerContainerRef}>
				{!showVideoUnavailable && (
					<VideoLayout
						videoTemplate={videoTemplate}
						videoPlayers={Object.fromEntries(
							Object.entries(sessionRecordingTimestamps).map(([typeKey, sourceTimestamps]) => {
								const type = typeKey as RecordingType;
								const sourceUrls = videos[type] ?? {};
								return [
									type,
									Object.entries(sourceUrls).map(([indexKey, sources]) => {
										const sourceIndex = Number(indexKey);
										return (
											<VideoCard
												key={indexKey}
												type={type === "webcam" && sourceIndex === 0 && showAudioThumbnail ? "microphone" : type}
												index={Object.keys(sourceUrls).length > 1 ? sourceIndex : undefined}
												onClick={setPlay}
											>
												<VideoJS
													events={events}
													debugging_string={type}
													dispatch={playbackDispatch}
													options={type === "webcam" ? webcamVideoOptions : screencaptureVideoOptions}
													controls={controlOptions}
													startDate={startDate}
													arrStamps={sourceTimestamps[sourceIndex] ?? [TIMESTAMP_PLACEHOLDER]}
													sources={sources}
													videoTimer={playback}
													displayStateAtom={type === "webcam" ? webcamDisplayStateAtom : screencaptureDisplayStateAtom}
													toggledTimestamps={showTimestamps}
													isFullscreen={isFullscreen}
													showAudioThumbnail={type === "webcam" && sourceIndex === 0 && showAudioThumbnail}
													isDeleted={isWebcamAndScreencaptureDeleted}
													deleteCause={webcamAndScreencaptureDeleteCause}
													isIncomplete={sessionStatus === "incomplete"}
													videoHasError={type === "webcam" ? webCamHasError : screenCaptureHasError}
													setVideoHasError={type === "webcam" ? setWebCamHasError : setScreenCaptureHasError}
													selectedProctoringSessionUuid={selectedProctoringSessionUuid}
													showMachineLearningOverlay={
														type === "webcam" && sourceIndex === 0 && showMachineLearningOverlay
													}
												/>
											</VideoCard>
										);
									}),
								];
							}),
						)}
					/>
				)}

				{
					// only show the overlay on the available recording. If both videos are available, show the overlay covering both videos.
					videoOverlayType === "both" && sessionStatus !== "incomplete" && (
						<PlayOverlay
							useVideoEndedOverlay={haveAllRecordingsFinishedPlaying}
							isOverlayVisible={isVideoPlayerOverlayVisible}
							setPlay={setPlay}
						/>
					)
				}

				{videoDisplayState === "ended" && (
					<div style={{ height: "100%" }}>
						<RecordingEndedSvg
							style={{
								height: "100%",
								width: "100%",
							}}
							endDate={videoEndDate ?? new Date(startDate.getTime() + (duration ?? 0))}
						/>
					</div>
				)}

				{videoDisplayState !== "ended" && showVideoUnavailable && (
					<button
						style={{
							height: "100%",
							width: "100%",
							justifyContent: "center",
							alignItems: "center",
							display: "flex",
							background: "none",
							border: "none",
							cursor: "pointer",
							outline: "none",
						}}
						tabIndex={-1}
						onClick={setPlay}
					>
						<img style={{ maxWidth: "100%", height: "100%" }} src={videoUnavailableSvg} alt="Video is unavailable" />
					</button>
				)}
			</div>
			{(videos.screencapture || videos.webcam) && (
				<VideoControlLayout inFullscreen={isFullscreen} controlContainerRef={controlContainerRef}>
					<PlayButton
						inFullscreen={isFullscreen}
						isPlaying={controlOptions.isPlaying}
						onClick={() => {
							// only enable playing if at least one video is available
							if (hasScreenRecordSource || hasWebcamSource) setPlay();
						}}
					/>
					<VolumeControls
						volume={controlOptions.volume}
						showVolumeInput={!isWebcamAndScreencaptureDeleted}
						inFullscreen={isFullscreen}
						onVolumeButtonClick={toggleVolume}
						onVolumeInputChange={setVolume}
					/>
					<VideoTimeStamp inFullscreen={isFullscreen}>
						{!showSeekbarTime
							? "0:00/0:00"
							: `${formatEventTimestamp(playback.timer - timeOffset, (duration ?? 0) - timeOffset)}/${formatEventTimestamp((duration ?? 0) - timeOffset, (duration ?? 0) - timeOffset)}`}
					</VideoTimeStamp>
					<Seekbar
						inFullscreen={isFullscreen}
						curSeekbarValue={playback.timer}
						minSeekbarValue={timeOffset}
						maxSeekbarValue={duration}
						onSeekbarChange={(e) => {
							playbackDispatch({
								type: "skip",
								time: e.target.valueAsNumber,
							});
						}}
						timeOffset={timeOffset}
					>
						<SeekbarEvents
							events={events}
							sessionNotes={sessionNotes}
							liveNotes={visibleLiveNotes}
							dispatch={playbackDispatch}
							duration={duration}
							globaltime={playback.timer / 1000}
							skip={playback.skip}
							offsetGetter={timeOffset}
							isFullscreen={isFullscreen}
						/>
					</Seekbar>

					<VideoControlButtonWrapper>
						<VideoSpeedButton
							menuShowing={showVideoSpeed}
							setMenuShowing={setShowVideoSpeed}
							playbackSpeed={controlOptions.playbackSpeed}
							controlContainerRef={controlContainerRef}
							onSelectSpeed={(speed) => setSpeed(speed.toString())}
							inFullscreen={isFullscreen}
						/>
						{shouldShowDownload && !(webCamHasError && screenCaptureHasError) && !isFullscreen && (
							<DownloadButton
								inFullscreen={isFullscreen}
								onClick={() => {
									setEmailModalOpen(true);
									onEmailModalChange?.(true);
								}}
							/>
						)}
						{!isFullscreen && (
							<DownloadRequestModal status={downloadRequestStatus} onClose={() => setDownloadRequestStatus("unsent")} />
						)}
						<FullScreenButton inFullscreen={isFullscreen} onClick={() => setFullscreen()} />

						{(hasScreenRecordSource || hasWebcamSource) &&
							!isWebcamAndScreencaptureDeleted &&
							!isFullscreen &&
							hasInstituteEditPermission &&
							sessionStatus !== "incomplete" && (
								<MoreOptionsButton
									menuShowing={menuShowing}
									setMenuShowing={setMenuShowing}
									inFullscreen={isFullscreen}
									onDeleteRecording={() => setIsDeleteConfirmationModalShown?.("recording")}
								/>
							)}

						{/* Settings button */}
						{showMachineLearningOverlay && (
							<VideoControlButton
								className={styles.settingsButton}
								onClick={() => setSettingsPopoverOpen(true)}
								aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.settings")}
								aria-haspopup
								aria-controls={settingsPopoverId}
								ref={settingsButtonRef}
							>
								<GearIcon className={styles.settingsIcon} />
							</VideoControlButton>
						)}
					</VideoControlButtonWrapper>
				</VideoControlLayout>
			)}

			{/* Settings popover */}
			<DetectionOverlaySettings
				accessibility={{
					id: settingsPopoverId,
				}}
				refs={{
					anchor: settingsButtonRef,
					container: playerContainerRef,
				}}
				state={{
					open: settingsPopoverOpen,
				}}
				hooks={{
					onClose: () => setSettingsPopoverOpen(false),
				}}
			/>
			{emailModalOpen && (
				<SendEmailModal
					onClose={() => {
						setEmailModalOpen(false);
						onEmailModalChange?.(false);
					}}
					onSend={(userEnteredEmail, userEnteredName) => {
						setDownloadRequestStatus("sending");
						void apiClient.download
							.requestProctoringSessionRecordingDownload(sessionId, userEnteredEmail, userEnteredName)
							.then(({ ready, emailDestination }) => {
								setDownloadRequestStatus({ status: "sent", ready, emailDestination });
							})
							.catch((err) => {
								console.error(err);
								setDownloadRequestStatus("failed");
							});
					}}
				/>
			)}
		</>
	);
}

export default ProctoringVideoPlayer;
